import React from 'react';
import styled from 'styled-components';
import LocationInterface from '@solvhealth/types/interfaces/Location';
import CircleImage from '../../../../../../../../SolvPatternLibrary/CircleImage';
import { getImageObject } from '../../../../../../../../SolvPatternLibrary/Picture/util';

import useSelectLocation from '../../../../../../../hooks/useSelectLocation';
import {
  isProviderGroupBooking,
  getProviderId,
  isPremiumBooking,
  isBookingCoveredByMembership,
} from '../../../../../../../../../core/util/booking';
import useFetchSingleProvider from '../../../../../../../../../components/ProviderGroup/hooks/useFetchSingleProvider';
import { ActiveBookingStates } from '~/components/AccountGetsMarried/components/GetCare/components/AccountHeadline/components/AppointmentCard/util';

interface IRoot {
  sc: any;
}

const Root = styled.div.withConfig({ componentId: 'sc-e03dix-0' })<IRoot>`
  margin: 0 ${({ sc }) => (sc.isProviderPhoto ? '20px' : '0')} 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledCircleImg = styled(CircleImage).withConfig({ componentId: 'sc-e03dix-1' })`
  height: 40px;
  width: 40px;
  object-fit: contain;
`;

const VisitIcon = styled.img.withConfig({ componentId: 'sc-e03dix-2' })`
  height: 40px;
  width: 40px;
  object-fit: contain;
`;

type Props = {
  bookingState: number;
  locationId: string;
  appointment: any;
};

const PostVisitIcon = ({
  booking,
  locationData,
}: {
  booking: any;
  locationData: LocationInterface;
}) => {
  if (isPremiumBooking(booking) && !isBookingCoveredByMembership(booking)) {
    return <VisitIcon alt="premium video visit icon" src="/images/account/camera_purple.png" />;
  }

  if (isPremiumBooking(booking) && isBookingCoveredByMembership(booking)) {
    return <VisitIcon alt="solv plus video visit icon" src="/images/account/camera_teal.png" />;
  }

  return (
    <StyledCircleImg
      imageObject={getImageObject(locationData, {
        showDefaultPictureIfMissing: true,
        imageIndex: 0,
      })}
    />
  );
};

const icon = (appointment: any, bookingState: number, locationData: LocationInterface) => {
  let calendarIcon = isBookingCoveredByMembership(appointment)
    ? '/images/account/plus_calendar.svg'
    : '/images/account/Card_upcoming_appt_calendar.svg';

  switch (bookingState) {
    case ActiveBookingStates.PreExternalTelemed:
    case ActiveBookingStates.PreTelemedIncompleteAndEarly:
    case ActiveBookingStates.PreTelemedIncompleteAndApptSoon:
    case ActiveBookingStates.PreTelemedCompleteAndEarly:
    case ActiveBookingStates.PreTelemedCompleteAndApptSoon:
    case ActiveBookingStates.PreClinicVisitHasCheckIncompleteAndEarly:
    case ActiveBookingStates.PreClinicVisitHasCheckCompleteOrApptSoon:
    case ActiveBookingStates.PreClinicVisitNoCheckIncomplete:
    case ActiveBookingStates.PreClinicVisitNoCheckComplete:
    case ActiveBookingStates.PreClinicVisitNoCheckNAPaperwork:
    case ActiveBookingStates.InTelemed:
    case ActiveBookingStates.InClinicVisit:
      return <VisitIcon alt="upcoming or current visit icon" src={calendarIcon} />;

    default:
      return <PostVisitIcon booking={appointment} locationData={locationData} />;
  }
};

const AppointmentIcon = (props: Props) => {
  const { bookingState, locationId, appointment } = props;
  const locationData = useSelectLocation(locationId);
  const { provider } = useFetchSingleProvider(getProviderId(appointment));

  return (
    <Root sc={{ isProviderPhoto: provider && isProviderGroupBooking(appointment) }}>
      {icon(appointment, bookingState, locationData)}
    </Root>
  );
};

export default AppointmentIcon;
